import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit'
import type { RootState } from '@/app/store'
import type {
  AuthState,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  UserData,
  InviteData,
  ChangeRoleRequest,
  ChangeRoleResponse,
} from '@/types/auth'
import * as authService from '@/lib/api/authService'
import { verifyUser } from '@/lib/api/userService'
import { setAuthToken } from '@/lib/api/apiClient'
import { mergeNotificationShownFlags, clearAnnouncementAcknowledgedSession } from '@/features/system-announcement/utils/mergeNotificationShownFlags'
import type { RoleSystemAnnouncement } from '@/features/system-announcement/types/roleNotification'
import {
  setStorageItem,
  removeStorageItem,
  getStorageJSON,
  setStorageJSON,
  getStorageItem,
  STORAGE_KEYS,
  getAuthTokenFromStorage,
} from '@/lib/utils/storage'
import { persistUserToStorage } from '@/features/auth/utils/postLoginFlow'
import { markSecurityNotificationPending } from '@/lib/auth/securityNotificationFlag'
import { resetSecurityCheckSession } from '@/components/SecurityNotificationBanner/securityNotificationCheck'

/**
 * Initial auth state
 */
const initialState: AuthState = {
  user: null,
  token: getAuthTokenFromStorage(),
  isAuthenticated: !!getAuthTokenFromStorage(),
  isLoading: false,
  error: null,
  inviteData: null,
  currentInviteId: getStorageItem(STORAGE_KEYS.inviteCode),
}

/**
 * Async thunk for fetching current user details
 */
export const fetchCurrentUser = createAsyncThunk<
  UserData,
  void,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const response = await verifyUser()
    return response
  } catch (error) {
    const apiError = error as { message: string }
    return rejectWithValue(apiError.message)
  }
})

/**
 * Async thunk for login
 */
export const loginUser = createAsyncThunk<
  LoginResponse,
  LoginRequest,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const response = await authService.login(credentials)
    return response
  } catch (error) {
    const apiError = error as { message: string; isRoleError?: boolean }
    return rejectWithValue(apiError.message)
  }
})

/**
 * Async thunk for registration
 */
export const registerUser = createAsyncThunk<
  LoginResponse,
  RegisterRequest,
  { rejectValue: string }
>('auth/register', async (userData, { rejectWithValue }) => {
  try {
    const response = await authService.registerAndLogin(userData)
    return response
  } catch (error) {
    const apiError = error as { message: string }
    return rejectWithValue(apiError.message)
  }
})

/**
 * Async thunk for logout
 */
export const logoutUser = createAsyncThunk<
  { success: boolean; message: string },
  void,
  { rejectValue: string }
>('auth/logout', async () => {
  try {
    const response = await authService.logout()
    return response
  } catch {
    // Even if API fails, we still want to logout locally
    // So we don't reject, just return a success response
    return { success: true, message: 'Logged out locally' }
  }
})

/**
 * Async thunk for changing user role
 */
export const changeRole = createAsyncThunk<
  ChangeRoleResponse,
  ChangeRoleRequest,
  { rejectValue: { message: string; status?: number } }
>('auth/changeRole', async (request, { rejectWithValue }) => {
  try {
    const response = await authService.changeRole(request)
    return response
  } catch (error) {
    const apiError = error as { message: string; status?: number }
    return rejectWithValue({
      message: apiError.message || 'Failed to change role',
      status: apiError.status,
    })
  }
})

/**
 * Auth slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Clear auth error
     */
    clearError: (state) => {
      state.error = null
    },

    setCurrentInviteId: (state, action: PayloadAction<string>) => {
      state.currentInviteId = action.payload
      setStorageItem(STORAGE_KEYS.inviteCode, action.payload)
    },

    setInviteData: (state, action: PayloadAction<InviteData>) => {
      state.inviteData = action.payload
      setStorageJSON(STORAGE_KEYS.invitationData, action.payload)
    },

    clearInviteData: (state) => {
      state.inviteData = null
      state.currentInviteId = null
      removeStorageItem(STORAGE_KEYS.invitationData)
      removeStorageItem(STORAGE_KEYS.inviteCode)
    },

    /**
     * Logout user (synchronous - for immediate state clearing)
     * The async logoutUser thunk should be used instead
     */
    logout: (state) => {
      state.user = null
      state.token = null
      state.isAuthenticated = false
      state.error = null
      state.inviteData = null
      state.currentInviteId = null
      clearAnnouncementAcknowledgedSession()
      resetSecurityCheckSession()
    },

    /**
     * Update user data
     */
    updateUser: (state, action: PayloadAction<UserData | Partial<UserData>>) => {
      if (action.payload && 'user_id' in action.payload) {
        // Full UserData object — preserve session isshown flags (Angular UserEffect).
        const incoming = action.payload as UserData
        const previousNotifications = state.user?.current_role?.notification
        const mergedUser =
          incoming.current_role?.notification
            ? {
                ...incoming,
                current_role: {
                  ...incoming.current_role,
                  notification: mergeNotificationShownFlags(
                    incoming.current_role.notification as RoleSystemAnnouncement[] | undefined,
                    previousNotifications as RoleSystemAnnouncement[] | undefined,
                  ),
                },
              }
            : incoming
        state.user = mergedUser
        // Always persist the MERGED user — callers that persist the raw verify
        // payload were wiping isshown and causing the popup to reopen.
        persistUserToStorage(mergedUser)
        if (state.token) {
          state.isAuthenticated = true
        }
      } else if (state.user) {
        // Partial update
        state.user = { ...state.user, ...action.payload }
      } else {
        // No existing user, but we have data - set it
        state.user = action.payload as UserData
        if (state.token) {
          state.isAuthenticated = true
        }
      }
    },

    /**
     * Set authentication directly from a token + user pair (impersonation swap).
     */
    setAuthFromToken: (state, action: PayloadAction<{ token: string; user: UserData }>) => {
      state.token = action.payload.token
      state.user = action.payload.user
      state.isAuthenticated = true
      state.error = null
    },

    /**
     * Restore auth from storage (for initial load)
     */
    restoreAuth: (state) => {
      const token = getAuthTokenFromStorage()
      const inviteCode = getStorageItem(STORAGE_KEYS.inviteCode)
      const invitationData = getStorageJSON<InviteData>(STORAGE_KEYS.invitationData)

      state.token = token
      state.isAuthenticated = !!token
      state.currentInviteId = inviteCode
      state.inviteData = invitationData

      // Restore token to API client
      if (token) {
        setAuthToken(token)
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user_data
        state.token = action.payload.token
        state.error = null
        persistUserToStorage(action.payload.user_data)
        // Angular login.component — flag for once-per-login security toast
        resetSecurityCheckSession()
        markSecurityNotificationPending()
        // Store role based on current_role
        const currentRole = action.payload.user_data.current_role?.role_name
        if (currentRole === 'ROLE_ADMIN') {
          setStorageItem(STORAGE_KEYS.userRole, 'admin')
        } else if (currentRole === 'ROLE_RENTER') {
        setStorageItem(STORAGE_KEYS.userRole, 'applicant')
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Login failed'
        state.isAuthenticated = false
        state.user = null
        state.token = null
      })

    // Fetch Current User
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        // Preserve session isshown flags across verify (Angular UserEffect).
        const incoming = action.payload
        const previousNotifications = state.user?.current_role?.notification
        const mergedUser =
          incoming.current_role?.notification
            ? {
                ...incoming,
                current_role: {
                  ...incoming.current_role,
                  notification: mergeNotificationShownFlags(
                    incoming.current_role.notification as RoleSystemAnnouncement[] | undefined,
                    previousNotifications as RoleSystemAnnouncement[] | undefined,
                  ),
                },
              }
            : incoming
        state.user = mergedUser
        state.error = null
        persistUserToStorage(mergedUser)

        // Update stored role if needed
        const currentRole = mergedUser.current_role?.role_name
        if (currentRole === 'ROLE_ADMIN') {
          setStorageItem(STORAGE_KEYS.userRole, 'admin')
        } else if (currentRole === 'ROLE_RENTER') {
          setStorageItem(STORAGE_KEYS.userRole, 'applicant')
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Failed to fetch user profile'
        // Don't clear auth here automatically as it might be a temporary network error
        // But if it's strictly an auth error (401), component logic might handle logout
      })

    // Register
    builder
      .addCase(registerUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.isAuthenticated = true
        state.user = action.payload.user_data
        state.token = action.payload.token
        state.error = null
        // Store role based on current_role
        const currentRole = action.payload.user_data.current_role?.role_name
        if (currentRole === 'ROLE_ADMIN') {
          setStorageItem(STORAGE_KEYS.userRole, 'admin')
        } else if (currentRole === 'ROLE_RENTER') {
        setStorageItem(STORAGE_KEYS.userRole, 'applicant')
        }
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload || 'Registration failed'
      })

    // Logout
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
        state.error = null
        clearAnnouncementAcknowledgedSession()
        resetSecurityCheckSession()
      })
      .addCase(logoutUser.rejected, (state) => {
        // Even if logout API fails, clear local state
        state.isLoading = false
        state.user = null
        state.token = null
        state.isAuthenticated = false
        clearAnnouncementAcknowledgedSession()
        resetSecurityCheckSession()
      })

    // Change Role
    builder
      .addCase(changeRole.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(changeRole.fulfilled, (state, action) => {
        state.isLoading = false
        state.error = null
        // Update user's current_role if role change was successful
        if (action.payload.allow_role_change && state.user && state.user.roles) {
          // Find the role that was changed to by looking at the request
          // We need to get the role_id from the action meta, but since we don't have it,
          // we'll update storage based on the role_name from the response or keep it as is
          // The actual navigation will handle setting the correct role in storage
        }
      })
      .addCase(changeRole.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload?.message || 'Failed to change role'
      })
  },
})

// Export actions
export const {
  clearError,
  logout,
  updateUser,
  restoreAuth,
  setAuthFromToken,
  setCurrentInviteId,
  setInviteData,
  clearInviteData,
} = authSlice.actions

// Export selectors
export const selectAuth = (state: RootState) => state.auth
export const selectUser = (state: RootState) => state.auth.user
export const selectToken = (state: RootState) => state.auth.token
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated
export const selectAuthLoading = (state: RootState) => state.auth.isLoading
export const selectAuthError = (state: RootState) => state.auth.error
export const selectCurrentInviteId = (state: RootState) => state.auth.currentInviteId
export const selectInviteData = (state: RootState) => state.auth.inviteData

// Export reducer
export default authSlice.reducer

